<template>
  <div>
    <ApiError :errors="errors"></ApiError>
    <AlertDisplay ref="alertRef" />
    <v-alert v-if="reportWarningMessage" type="warning" border="top" dense>
      <div class="d-flex align-center">
        <span>{{ reportWarningMessage }}</span>
        <v-spacer></v-spacer>
        <v-btn v-if="dataLoadFailed" text small :loading="isLoadingRows" class="ml-2" @click="fetchCustomReport">
          Retry data
        </v-btn>
        <v-btn v-if="countFailed" text small :loading="isLoadingCount" class="ml-2" @click="fetchCustomReportCount">
          Retry count
        </v-btn>
      </div>
    </v-alert>
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="isLoadingRows"
      :server-items-length="serverItemsLength"
      :options.sync="options"
      :hide-default-footer="hideFooter"
      :footer-props="{ disable: isLoadingRows }"
    >
      <template v-slot:top>
        <v-toolbar flat color="white">
          <v-toolbar-title v-if="customReport">
            {{ customReportConfiguration.id != null ? customReportConfiguration.name : customReport.name }}
            <v-subheader class="pa-0">{{
              customReportConfiguration.id != null ? customReportConfiguration.description : customReport.description
            }}</v-subheader>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="mx-1"
                fab
                small
                v-bind="attrs"
                v-on="on"
                :disabled="isGeneratingReport || items.length == 0 || isLoadingRows"
                @click="onFavoriteShow"
              >
                <v-icon v-if="isFavorite">mdi-heart-cog</v-icon>
                <v-icon v-else>mdi-heart-outline</v-icon>
              </v-btn>
            </template>
            <span v-if="isFavorite">{{ $i18n.translate("Configure Favorite") }}</span>
            <span v-else>{{ $i18n.translate("Add Favorite") }}</span>
          </v-tooltip>
          <v-tooltip bottom v-if="customReport && customReportFilters && customReportFilters.length > 0 && showFilters">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="mx-1"
                fab
                small
                @click="onCloseFilters()"
                v-bind="attrs"
                v-on="on"
                :disabled="isGeneratingReport || isLoadingRows"
              >
                <v-icon>mdi-filter</v-icon>
              </v-btn>
            </template>
            <span>{{ $i18n.translate("Hide Filters") }}</span>
          </v-tooltip>
          <v-tooltip
            bottom
            v-else-if="customReport && customReportFilters && customReportFilters.length > 0 && !showFilters"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="mx-1"
                fab
                small
                @click="onOpenFilters()"
                v-bind="attrs"
                v-on="on"
                :disabled="isGeneratingReport || (items.length == 0 && activeFilterCount == 0) || isLoadingRows"
              >
                <v-icon>mdi-filter-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $i18n.translate("Show Filters") }}</span>
          </v-tooltip>
          <GenerationFileType
            :isGeneratingReport="isGeneratingReport"
            :itemsLength="items.length"
            :disabled="isLoadingRows"
            @generateReport="onGenerateReport"
          />
          <v-tooltip bottom v-if="isLoadingCount">
            <template v-slot:activator="{ on, attrs }">
              <v-progress-circular class="mx-2" size="22" width="2" indeterminate v-bind="attrs" v-on="on" />
            </template>
            <span>Calculating total row count</span>
          </v-tooltip>
        </v-toolbar>
        <div v-if="customReport" class="mb-4">
          <v-expansion-panels
            v-if="customReportFilters && customReportFilters.length > 4 && activeFilterCount > 4"
            class="mb-2"
          >
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div class="d-flex align-center">
                  <v-icon small class="mr-2" :color="activeFilterCount > 0 ? 'primary' : ''">
                    mdi-filter
                  </v-icon>
                  <span class="subtitle-2">
                    Active Filters
                  </span>
                  <v-chip v-if="activeFilterCount > 0" small color="primary" class="ml-2">
                    {{ activeFilterCount }}
                  </v-chip>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <v-divider></v-divider>

                <div class="pa-3">
                  <div class="d-flex flex-wrap align-center">
                    <CustomReportFilterChip
                      v-for="(filter, index) in customReportFilters"
                      :key="filter.id"
                      :filter="filter"
                      closable
                      @remove="onCloseFilter(index)"
                      class="mr-2 mb-2"
                    />
                    <v-btn
                      v-if="activeFilterCount > 1"
                      text
                      small
                      color="primary"
                      @click="clearAllFilters"
                      class="ml-2 mb-2 pa-5"
                    >
                      Clear All
                    </v-btn>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <div
            v-else-if="
              customReportFilters && customReportFilters.length > 0 && activeFilterCount > 0 && activeFilterCount <= 4
            "
          >
            <CustomReportFilterChip
              v-for="(filter, index) in customReportFilters"
              :key="filter.id"
              :filter="filter"
              closable
              @remove="onCloseFilter(index)"
              class="mr-2 mb-1"
            />
          </div>
        </div>
        <v-card v-show="showFilters" elevation="10">
          <v-toolbar flat>
            <v-toolbar-title>
              <span>Apply Filters</span>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              v-if="customReportFilters.length > 4"
              small
              color="primary"
              class="mr-2"
              :disabled="isGeneratingReport"
              @click="onSubmit()"
            >
              {{ $i18n.translate("Apply") }}
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on: on }">
                <v-btn icon v-on="on" @click="onCloseFilters()">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>{{ $i18n.translate("Close") }}</span>
            </v-tooltip>
          </v-toolbar>
          <v-card-text>
            <v-form v-model="valid" @submit.prevent="onSubmit">
              <v-simple-table dense v-if="customReport">
                <template v-slot:default>
                  <tbody>
                    <tr v-for="(customReportFilter, index) in customReportFilters" :key="customReportFilter.id">
                      <th width="0%" class="text-right">
                        {{ customReportFilter.name }}
                        {{ customReportFilter?.filterType?.matchingName }}
                      </th>
                      <td width="60%" class="pa-3">
                        <CustomReportFilterFormField
                          :key="refresh"
                          outlined
                          dense
                          v-model="customReportFilters[index]"
                          clearable
                          :disabled="isGeneratingReport"
                          hide-details
                        />
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <div class="text-center py-5">
                <v-btn type="submit" small color="primary" :disabled="isGeneratingReport">
                  {{ $i18n.translate("Apply") }}
                </v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>
        <v-card v-if="showFavorite" elevation="10">
          <v-toolbar flat>
            <v-toolbar-title>
              <span>Save Report as Favorite</span>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <template v-slot:activator="{ on: on }">
                <v-btn icon v-on="on" @click="onFavoriteClose">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>{{ $i18n.translate("Close") }}</span>
            </v-tooltip>
          </v-toolbar>
          <v-card-text>
            <v-form ref="favoriteForm" v-model="valid" @submit.prevent="addFavorite">
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <th class="text-right">Your name for Report</th>
                      <td class="pa-3">
                        <v-text-field
                          v-model="customReportConfiguration.name"
                          :rules="rules.name"
                          persistent-hint
                          :hint="`Originally '${customReport.name}'`"
                          class="required"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th class="text-right">Your description for Report</th>
                      <td class="pa-3">
                        <v-text-field
                          v-model="customReportConfiguration.description"
                          :rules="rules.description"
                          persistent-hint
                          :hint="`Originally '${customReport.description}'`"
                          class="required"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th class="text-right">Send Report to My Email?</th>
                      <td class="pa-3">
                        <span v-if="!selectedParticipant.email1" class="red--text"
                          >You must set an email address in your profile to enable scheduling.</span
                        >
                        <span v-else>
                          <v-switch
                            v-model="customReportConfiguration.scheduled"
                            :label="selectedParticipant.email1"
                            class="required"
                          />
                          <div v-if="customReportConfiguration.scheduled">
                            <CronField
                              v-model="customReportConfiguration.scheduledCronExpression"
                              :noZeroMinute="true"
                              :noHourly="true"
                            />
                            <CustomReportGenerationTypeField
                              v-model="customReportConfiguration.generationType"
                              label="Generation type"
                              class="required"
                              :rules="rules.generationType"
                            />
                            <MultiEmailField
                              ref="emailCcField"
                              name="emailCc"
                              label="Cc"
                              v-model="customReportConfiguration.emailCc"
                              dense
                            />
                            <MultiEmailField
                              ref="emailBccField"
                              name="emailBcc"
                              label="Bcc"
                              v-model="customReportConfiguration.emailBcc"
                              dense
                            />
                          </div>
                        </span>
                      </td>
                    </tr>

                    <tr v-if="customReportFilters && customReportFilters.length > 0">
                      <th class="text-right">Your applied filters for Report</th>
                      <td class="pa-3">
                        <CustomReportFilterChip
                          v-for="filter in customReportFilters"
                          :key="filter.id"
                          :filter="filter"
                        />
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <div class="text-center py-5">
                <v-btn type="submit" class="mx-5" small color="primary" :disabled="!valid"> Save</v-btn>
                <v-btn
                  v-if="customReportConfiguration.id"
                  class="mx-5"
                  type="button"
                  small
                  color="primary"
                  @click="removeFavorite"
                >
                  Delete
                </v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>
      </template>
      <template v-slot:no-data>
        <p class="mt-5">
          No report data found
        </p>
        <p v-if="hasFilters">Refine your filters to see more data</p>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import AlertDisplay from "@/gapp-components/components/display/AlertDisplay.vue";
import ApiError from "@/gapp-components/components/display/ApiError";
import CustomReportFilterFormField from "@/gapp-components/components/fields/CustomReportFilterFormField.vue";
import GenerationFileType from "@/gapp-components/components/tables/customReports/GenerationFileType.vue";
import ApiService from "@/gapp-components/services/api.service";
import ValidationService from "@/gapp-components/services/validation.service.js";
import Vue from "vue";
import { mapGetters } from "vuex";
import CronField from "../fields/CronField.vue";
import CustomReportGenerationTypeField from "@/gapp-components/components/fields/CustomReportGenerationTypeField";
import MultiEmailField from "@/gapp-components/components/fields/MultiEmailField";
import CustomReportFilterChip from "@/gapp-components/components/common/reports/CustomReportFilterChip";

import UtilService from "@/gapp-components/services/util.service.js";

export default {
  name: "CustomReportTable",
  props: {
    customReportId: Number,
    customReportConfigurationId: {
      type: Number,
      required: false,
      default: undefined
    }
  },
  components: {
    ApiError,
    AlertDisplay,
    CustomReportFilterFormField,
    GenerationFileType,
    CronField,
    CustomReportGenerationTypeField,
    MultiEmailField,
    CustomReportFilterChip
  },
  data: function() {
    return {
      valid: false,
      refresh: 0,
      isLoadingRows: true,
      isLoadingCount: false,
      showFilters: false,
      errors: {},
      dataLoadFailed: false,
      countFailed: false,
      customReport: undefined,
      headers: [],
      items: [],
      isGeneratingReport: false,
      errorMessage: "",
      total: null,
      options: {
        itemsPerPage: 10
      },
      hideFooter: true,
      filters: [],
      customReportFilters: [],
      customReportSorts: [],
      rules: {
        name: [
          ValidationService.generateNotWhitespacesRule("Report Name"),
          ...ValidationService.generateTextValidationRules("Report Name")
        ],
        generationType: [v => !!v || "Generation Type is required"],
        description: [
          ValidationService.generateNotWhitespacesRule("Report Description"),
          ...ValidationService.generateTextValidationRules("Report Description")
        ]
      },
      customReportConfiguration: {
        id: null,
        name: "",
        description: "",
        scheduled: false,
        scheduledCronExpression: "0 * * * *",
        generationType: { name: "CSV" }
      },
      isFavorite: false,
      showFavorite: false
    };
  },
  watch: {
    customReportId() {
      this.resetReportResults();
      this.fetchCustomReportConfiguration();
    },
    "customReportConfiguration.scheduled"(val) {
      if (val) {
        if (this.$refs && this.$refs.favoriteForm) {
          this.$nextTick(() => {
            this.$refs.favoriteForm.validate();
          });
        }
      }
    },
    options: {
      handler() {
        this.customReportConfiguration.id = this.customReportConfigurationId;
        if (this.customReport) {
          this.fetchCustomReport();
        } else {
          this.fetchCustomReportConfiguration();
        }
      },
      deep: true
    }
  },
  methods: {
    applyFilters() {
      this.filters = this.customReportFilters
        .map(filter => {
          if (filter.value && filter.value.id) {
            return { id: filter.id, value: filter.value.id };
          } else {
            return { id: filter.id, value: filter.value };
          }
        })
        .filter(filter => filter.value != null && filter.value != undefined);
    },
    applySorts() {
      let headers = this.customReport?.customReportHeaders || [];
      this.customReportSorts = this.options.sortBy.map((sortBy, i) => {
        let sortField = headers.find(h => h.name === sortBy)?.sqlSortColumn || sortBy;
        let direction = this.options.sortDesc[i] ? "DESC" : "ASC";
        return { sortField, direction };
      });
    },

    onSubmit() {
      this.applyFilters();
      this.onCloseFilters();
      this.dataLoadFailed = false;
      this.invalidateCount();
      this.fetchCustomReportAndCount();
    },

    onCloseFilter(index) {
      Vue.set(this.customReportFilters[index], "value", null);
      this.refresh++;
      this.onSubmit();
    },
    onOpenFilters() {
      this.showFavorite = false;
      this.showFilters = true;
    },
    onCloseFilters() {
      this.showFilters = false;
    },
    onGenerateReport(generationType) {
      this.errors = {};
      this.isGeneratingReport = true;
      this.applyFilters();
      this.applySorts();

      ApiService.post(`/api/customReports/generate/${this.customReportId}/${generationType}`, {
        customReportFilters: this.filters,
        customReportSorts: this.customReportSorts
      })
        .then(() => {
          this.$router.push({ name: "reportDownloads" });
        })
        .catch(error => {
          this.isGeneratingReport = false;
          if (error.response && error.response.status === 404) {
            this.errorMessage = "Report generation failed. The specified report was not found.";
          } else {
            this.errorMessage = "An unexpected error occurred while generating the report. Please try again.";
          }
        })
        .finally(() => {
          this.isGeneratingReport = false;
          this.loading = false;
        });
    },
    onFavoriteShow() {
      if (!this.customReportConfiguration.id) {
        this.customReportConfiguration.name = this.customReport.name;
        this.customReportConfiguration.description = this.customReport.description;
      }

      this.showFavorite = !this.showFavorite;
      this.showFilters = false;
      if (this.$refs && this.$refs.favoriteForm) {
        this.$nextTick(() => {
          this.$refs.favoriteForm.validate();
        });
      }
    },
    onFavoriteClose() {
      this.showFavorite = false;
    },
    addFavorite() {
      this.$refs.emailCcField?.$refs?.combobox?.blur();
      this.$refs.emailBccField?.$refs?.combobox?.blur();

      // used twice to ensure the latest v-combobox value is fully updated before saving.
      this.$nextTick(() => {
        this.$nextTick(() => {
          const isValid = this.$refs.favoriteForm.validate();
          if (!isValid) return;
          let hasEmail = this.selectedParticipant.email1 && this.selectedParticipant.email1.trim().length > 0;

          let postForm = {
            name: this.customReportConfiguration.name,
            description: this.customReportConfiguration.description,
            scheduled: hasEmail ? this.customReportConfiguration.scheduled : false,
            scheduledCronExpression: hasEmail ? this.customReportConfiguration.scheduledCronExpression : null,
            customReportFilterValues: this.filters.map(filter => ({
              customReportFilter: { id: filter.id },
              selectedValue: filter.value
            })),
            generationType: this.customReportConfiguration.generationType,
            emailCc: this.customReportConfiguration.emailCc,
            emailBcc: this.customReportConfiguration.emailBcc
          };

          let serviceCall = null;
          if (this.customReportConfiguration.id !== undefined) {
            serviceCall = this.$api.post(
              `/api/customReportConfigurations/${this.customReportConfiguration.id}/update`,
              postForm
            );
          } else {
            postForm.participant = { id: this.selectedParticipant.id };
            postForm.customReport = { id: this.customReportId };
            serviceCall = this.$api.post("/api/customReportConfigurations", postForm);
          }

          if (serviceCall !== null) {
            serviceCall
              .then(data => {
                this.isFavorite = true;
                this.customReportConfiguration.id = data?.data?.id;
                this.onFavoriteClose();
                this.$refs.alertRef.success("Saved favorite report");
                this.$router
                  .push({
                    name: "report",
                    params: { id: this.customReportId },
                    query: { favorite: this.customReportConfiguration.id }
                  })
                  .catch(() => {});
              })
              .catch(error => {
                this.errors = ApiService.getErrorsFromResponse(error);
                console.error("Favorite report error:", error);
              });
          }
        });
      });
    },
    removeFavorite() {
      if (this.customReportConfiguration.id !== undefined) {
        this.$api
          .delete(`/api/customReportConfigurations/${this.customReportConfiguration.id}/delete`)
          .then(() => {
            this.$refs.alertRef.success("Deleted favorite report");
            this.customReportConfiguration.id = undefined;
            this.isFavorite = false;
            this.customReportConfiguration.scheduled = false;
            this.customReportConfiguration.name = "";
            this.customReportConfiguration.description = "";
            this.onFavoriteClose();
            this.$router.push({ name: "report", params: { id: this.customReportId } }).catch(() => {});
          })
          .catch(error => {
            this.errors = ApiService.getErrorsFromResponse(error);
            console.error("Favorite report error:", error);
          });
      }
    },
    hasFilters() {
      return (
        this.customReportFilters.filter(
          filter => filter.value != null && filter.value != undefined && filter.value.trim().length > 0
        ).length > 0
      );
    },
    isEmpty(value) {
      if (typeof value === "string") {
        return value === null || value === undefined || value.trim().length == 0;
      }
      return value === null || value === undefined;
    },
    fetchHeadersAndFilters() {
      if (!this.customReport) {
        return;
      }

      this.headers = this.customReport.customReportHeaders.map(customReportHeader => {
        return {
          text: customReportHeader.name,
          value: customReportHeader.name,
          sortable: customReportHeader.allowSort,
          align:
            customReportHeader.alignment && customReportHeader.alignment.name
              ? customReportHeader.alignment.name.toLowerCase()
              : "left"
        };
      });

      if (this.customReportFilters.length == 0) {
        this.customReportFilters = this.customReport.customReportFilters;
      }
    },

    fetchCustomReportAndCount() {
      return Promise.all([this.fetchCustomReport(), this.fetchCustomReportCount()]);
    },

    invalidateCount() {
      this.total = null;
      this.countFailed = false;
      this.$emit("total", null);
    },

    resetReportResults() {
      this.customReport = undefined;
      this.items = [];
      this.errors = {};
      this.dataLoadFailed = false;
      this.invalidateCount();
    },

    fetchCustomReport() {
      if (!this.customReportId) {
        return Promise.reject("No custom report id provided");
      }

      this.errors = {};
      this.isLoadingRows = true;
      const params = UtilService.buildPaginationParams(this.options);

      return ApiService.post(`/api/customReports/view/${this.customReportId}?${params.toString()}`, {
        customReportFilters: this.filters
      })
        .then(({ data }) => {
          this.dataLoadFailed = false;
          this.customReport = data.customReport;
          this.hideFooter = !this.customReport.paging;
          this.items = data.content;

          this.fetchHeadersAndFilters();

          this.$emit("name", this.customReport.name);
        })
        .catch(error => {
          if (error?.response?.status === 504) {
            this.dataLoadFailed = true;
            this.items = [];
          } else {
            this.errors = ApiService.getErrorsFromResponse(error);
          }
        })
        .finally(() => {
          this.isLoadingRows = false;
        });
    },

    fetchCustomReportCount() {
      if (!this.customReportId) {
        return Promise.resolve();
      }

      this.isLoadingCount = true;
      return ApiService.post(`/api/customReports/view/${this.customReportId}/count`, {
        customReportFilters: this.filters
      })
        .then(({ data }) => {
          this.countFailed = false;
          this.total = data.totalRows;
          this.$emit("total", this.total);
        })
        .catch(error => {
          this.total = null;
          this.$emit("total", null);
          if (error?.response?.status === 504) {
            this.countFailed = true;
          } else {
            this.errors = ApiService.getErrorsFromResponse(error);
          }
        })
        .finally(() => {
          this.isLoadingCount = false;
        });
    },

    fetchCustomReportConfiguration() {
      if (!this.customReportId) {
        return Promise.resolve();
      }

      if (!this.customReportConfiguration.id) {
        return this.fetchCustomReportAndCount();
      }

      const params = {
        participant: {
          id: this.selectedParticipant.id
        },
        customReport: {
          id: this.customReportId
        },
        id: this.customReportConfiguration.id ?? null
      };
      return ApiService.post(
        `/api/customReportConfigurations/search?expand=customReportFilterValues,customReportFilterValues.customReportFilter`,
        params
      )
        .then(({ data }) => {
          let content = data.content;
          if (content.length > 0) {
            this.isFavorite = true;
            this.customReportConfiguration.name = content[0].name;
            this.customReportConfiguration.description = content[0].description;
            this.customReportConfiguration.scheduled = content[0].scheduled;
            this.customReportConfiguration.scheduledCronExpression = content[0].scheduledCronExpression || "0 * * * *";
            this.customReportFilters = Array.isArray(content[0].customReportFilterValues)
              ? content[0].customReportFilterValues.map(filter => ({
                  id: filter.customReportFilter.id,
                  name: filter.customReportFilter.name,
                  filterType: filter.customReportFilter.filterType,
                  matchingName: filter.customReportFilter.filterType?.matchingName,
                  value: filter.selectedValue
                }))
              : [];
            this.customReportConfiguration.generationType = content[0].generationType;
            this.customReportConfiguration.emailCc =
              content[0].emailCc && content[0].emailCc.length > 0 ? content[0].emailCc : undefined;
            this.customReportConfiguration.emailBcc =
              content[0].emailBcc && content[0].emailBcc.length > 0 ? content[0].emailBcc : undefined;

            this.filters = this.customReportFilters;
            if (this.customReportConfiguration.id === undefined) {
              this.customReportConfiguration.id = content[0].id;
            }
          } else {
            this.isFavorite = false;
          }
        })
        .catch(error => {
          this.errors = ApiService.getErrorsFromResponse(error);
        })
        .finally(() => {
          return this.fetchCustomReportAndCount();
        });
    },
    clearAllFilters() {
      this.customReportFilters.forEach((filter, index) => {
        Vue.set(this.customReportFilters[index], "value", null);
      });
      this.refresh++;
      this.onSubmit();
    }
  },
  computed: {
    ...mapGetters(["selectedClient", "selectedParticipant"]),

    serverItemsLength() {
      return this.total ?? 0;
    },

    activeFilterCount() {
      return this.customReportFilters.filter(
        filter =>
          filter.value != null &&
          filter.value !== undefined &&
          (typeof filter.value !== "string" || filter.value.trim().length > 0)
      ).length;
    },

    reportWarningMessage() {
      if (!this.dataLoadFailed && !this.countFailed) {
        return "";
      }

      let prefix = "";
      if (this.dataLoadFailed && this.countFailed) {
        prefix = "We could not load the report data or calculate the total number of rows. ";
      } else if (this.dataLoadFailed) {
        prefix =
          this.total != null
            ? "The total number of rows was calculated, but we could not load the report data. "
            : "We could not load the report data. ";
      } else {
        prefix =
          this.items && this.items.length > 0
            ? "The report data was loaded, but we could not calculate the total number of rows. "
            : "We could not calculate the total number of rows. ";
      }

      return (
        prefix +
        "This report may contain a large volume of data. " +
        "You can retry the request or apply more restrictive filters to reduce the number of matching rows."
      );
    }
  }
};
</script>
